import { ReactElement, useState, useContext } from 'react';
import { notification, Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { INTAKE_THANK_YOU } from 'constants/pathNames';
import AirtableService from 'services/airtable';
import styles from './IntakePage.module.scss';
import { UserContext } from 'contexts';
import IntakeForm from 'components/IntakeForm';
import postUserData from 'utils/api/postUserData';
import { fbAnalytics } from 'utils/analytics';
import { GTM_EVENTS } from 'constants/analytics';

const IntakePage = (): ReactElement => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { lastSelectedJob } = useContext(UserContext);
  const [loadingButton, setLoadingButton] = useState(false);

  const handleSubmitForm = async (): Promise<void> => {
    setLoadingButton(true);
    try {
      const formFields = form.getFieldsValue();
      const airtableResponse = await AirtableService.createRecord({
        typecast: true,
        tableName: 'Jobseekers',
        viewName: 'All candidates',
        fields: { ...formFields, 'Source Platform': 'join.joblio intake' },
      });

      localStorage.setItem('airtableUserId', airtableResponse.id);
      const userId = airtableResponse.id;

      fbAnalytics(GTM_EVENTS.INTAKE_FORM, {
        jobTitle: form.getFieldValue('Looking for a job in the following areas'),
        jobDepartment: form.getFieldValue('Profession'),
        jobLocation: form.getFieldValue('Currently Residing'),
        jobCountry: form.getFieldValue('Citizenship'),
      });

      if (lastSelectedJob?.jobId && userId) {
        const applicationResponse = await postUserData(`/users/${userId}/applications`, { job_id: lastSelectedJob.jobId });

        notification.success({
          message: 'You\'ve successfully applied for a job',
          placement: 'topRight',
          duration: 5,
        });
        navigate(`/questionnaire/${lastSelectedJob.jobId}/${userId}/${applicationResponse.applicationRecordId}`, { replace: true });
      } else {
        navigate(INTAKE_THANK_YOU);
      }
    } catch (error) {
      console.error(error);
      notification.error({
        message: 'Failed to process intake form',
        description: 'Please try again later',
        placement: 'topRight',
        duration: 5,
      });
    } finally {
      setLoadingButton(false);
    }
  };

  return (
    <div className="container container-small">
      <h2 className="title"><FormattedMessage id="intake.contentTitle" /></h2>
      <p className={styles.textMargin}><FormattedMessage id="intake.contentText" /></p>
      <IntakeForm form={form} submitForm={handleSubmitForm} loading={loadingButton} />
    </div>
  );
};

export default IntakePage;
